import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, Tag, Button, Input, Select, DatePicker, Row, Col, Space } from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const TeamInviteHistory = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({
    status: '',
    player_name: '',
    start_date: '',
    end_date: ''
  });
  const [historyType, setHistoryType] = useState('sent');

  useEffect(() => {
    // Verificar parâmetro da URL
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('type') || 'sent';
    setHistoryType(type);
    loadInvites();
  }, [pagination.current, pagination.pageSize, filters, historyType]);

  const loadInvites = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };

      const endpoint = historyType === 'received' ? 
        '/community/invite-history/team?type=received' : 
        '/community/invite-history/team?type=sent';
        
      const response = await api.get(endpoint, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setInvites(response.data.invites);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
    setLoading(false);
  };

  const handleTableChange = (pag) => {
    setPagination(pag);
  };

  const handleDateChange = (dates) => {
    setFilters(prev => ({
      ...prev,
      start_date: dates?.[0]?.format('YYYY-MM-DD') || '',
      end_date: dates?.[1]?.format('YYYY-MM-DD') || ''
    }));
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      ACCEPTED: 'green',
      REJECTED: 'red',
      CANCELLED: 'gray'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      PENDING: 'Pendente',
      ACCEPTED: 'Aceito',
      REJECTED: 'Recusado',
      CANCELLED: 'Cancelado'
    };
    return texts[status] || status;
  };

  const columns = [
    {
      title: 'Jogador',
      dataIndex: 'player_name',
      key: 'player_name',
    },
    {
      title: 'Posição',
      dataIndex: 'preferred_position',
      key: 'preferred_position',
    },
    {
      title: 'Nível',
      dataIndex: 'skill_level',
      key: 'skill_level',
      render: (level) => <Tag color="blue">{level}</Tag>
    },
    {
      title: 'Data do Jogo',
      dataIndex: 'game_date',
      key: 'game_date',
      render: (date) => date || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'Enviado em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm')
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => window.history.back()}
            style={{ marginRight: 16 }}
          >
            Voltar
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {historyType === 'received' ? 'Histórico de Candidaturas Recebidas' : 'Histórico de Convites Enviados'}
          </Title>
        </div>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Input
              placeholder="Nome do jogador"
              prefix={<SearchOutlined />}
              value={filters.player_name}
              onChange={(e) => setFilters(prev => ({ ...prev, player_name: e.target.value }))}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Status"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              allowClear
            >
              <Option value="PENDING">Pendente</Option>
              <Option value="ACCEPTED">Aceito</Option>
              <Option value="REJECTED">Recusado</Option>
              <Option value="CANCELLED">Cancelado</Option>
            </Select>
          </Col>
          <Col xs={24} sm={10}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleDateChange}
              placeholder={['Data inicial', 'Data final']}
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={invites}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} convites`
          }}
          onChange={handleTableChange}
          rowKey="id"
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default TeamInviteHistory;